import {Component, ElementRef, inject, signal, ViewChild, WritableSignal} from '@angular/core';
import {MatPaginator} from '@angular/material/paginator';
import {MatSort} from '@angular/material/sort';
import {MatTableDataSource} from '@angular/material/table';
import {Router} from '@angular/router';
import {IReportNavigationConfig} from 'frontend/src/app/interfaces/report-navigation-config.interface';
import {EventSummaryFilters} from '../../../system-event/classes/event-summary-filters';
import {BaseWidget} from '../../classes/base-widget';
import {exportHeadoutAsCsv} from '../../../../integrations/headout/functions/export-headout-csv';
import {headoutTableColumnNames, HeadoutTableSort} from '../../../../integrations/headout/functions/table-utils';
import {HeadoutTableData} from '../../../../integrations/headout/interfaces/headout-table-data.interface';
import {HeadoutEventResult} from '../../../../integrations/headout/classes/headout-event-result';
import {LoadingMaskOptions} from '../../../../classes/loading-mask-options';
import {filter, switchMap} from 'rxjs/operators';
import {HeadoutService} from '../../../../integrations/headout/headout.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {GenSystemEventAnalyticHandle} from '../../../../../../generated/serverModels/GenSystemEventAnalyticHandle';
import {FormControl} from '@angular/forms';
import {HeadoutEventResultItem} from '../../../../integrations/headout/classes/headout-event-result-item';

@Component({
    selector: 'eaglei-headout-table-widget',
    templateUrl: './headout-table-widget.component.html',
    styleUrls: ['./headout-table-widget.component.scss'],
    standalone: false,
})
export class HeadoutTableWidgetComponent extends BaseWidget {
    @ViewChild(MatSort) sort: MatSort;
    @ViewChild(MatPaginator) paginator: MatPaginator;

    private headoutService = inject(HeadoutService);

    public readonly columnNames: string[] = headoutTableColumnNames;
    public dataSource: MatTableDataSource<HeadoutTableData>;
    public advisories: WritableSignal<HeadoutEventResult[]> = signal([]);
    public maskOptions: LoadingMaskOptions = new LoadingMaskOptions();

    protected advisoryControl = new FormControl<HeadoutEventResult>(null);

    constructor(private widgetElement: ElementRef, private router: Router) {
        super(widgetElement);

        this.advisoryControl.valueChanges
            .pipe(
                filter((advisory) => !!advisory),
                switchMap((advisory: HeadoutEventResult) => this.headoutService.getEventResults(advisory)),
                takeUntilDestroyed()
            )
            .subscribe((outages: HeadoutEventResultItem[]) => {
                this.initializeData(outages.map((o) => HeadoutEventResultItem.ConvertToReportData(o)));
            });
    }

    /**
     * On event filter change, if the selected event is a headout event, all advisories will be fetched and the most
     * recent is set to the active table data.
     * @param eventFilters New filter values emitted from the event dashboard.
     * @protected
     */
    protected handleEventFilterChange(eventFilters: EventSummaryFilters) {
        this.maskOptions.resetMask();

        if (!eventFilters.event.hasAnalytic(GenSystemEventAnalyticHandle.HEADOUT)) {
            this.maskOptions.displayMask('This event does not have headout information');
            this.advisories.set([]);
            this.advisoryControl.reset();
            return;
        }

        this.maskOptions.displayMask();
        this.headoutService.getListResults(eventFilters.event).subscribe((advisories: HeadoutEventResult[]) => {
            this.advisories.set(advisories);
            this.advisoryControl.setValue(advisories[advisories.length - 1]);
            this.maskOptions.resetMask();
        });
    }

    /**
     * Abstract function that is called after ngOnDestroy
     */
    destroyWidget(): void {}

    /**
     * Initialized the data source for the table, if the data source already exists, the data will be updated, otherwise
     * the source will be created, and the paging, sorting, and sort logic will be applied.
     * @param data an array of @HeadoutTableData that will be displayed in the table.
     * @private
     */
    private initializeData(data: HeadoutTableData[]): void {
        if (this.dataSource) {
            this.dataSource.data = data;
        } else {
            this.dataSource = new MatTableDataSource<HeadoutTableData>(data);
            this.dataSource.paginator = this.paginator;
            this.dataSource.sortingDataAccessor = HeadoutTableSort;
            this.dataSource.sort = this.sort;
        }
    }

    /**
     * Exports the current data in the table as a CSV file.
     */
    public exportTable() {
        exportHeadoutAsCsv(this.dataSource._orderData(this.dataSource.filteredData), 'HEADOUT_event', this.attributionUrl);
    }

    /**
     * Navigates to the headout report
     */
    public goToReport(): void {
        const filters: IReportNavigationConfig[] = [];
        // noinspection JSIgnoredPromiseFromCall
        this.router.navigate(['app/reports/headoutEvent'], {state: {filters}});
    }
}
